import React, { useState } from "react";
import { AlbumLink } from "../../types/album";
import LoadingSpinner from "./LoadingSpinner";
import { buildBrowserShareUrl } from "../../utils/shareLinks";
import OverlayRoot from "./OverlayRoot";

interface ShareLinksModalProps {
  isOpen: boolean;
  onClose: () => void;
  albumName: string;
  albumId: string;
  ownerId: string;
  links: AlbumLink[];
  isLoading: boolean;
  isCreating: boolean;
  isDeleting: boolean;
  error: string | null;
  onCreateLink: (permission: "read_only" | "upload") => Promise<void>;
  onDeleteLink: (linkId: string) => Promise<void>;
  onClearError: () => void;
}

export const ShareLinksModal: React.FC<ShareLinksModalProps> = ({
  isOpen,
  onClose,
  albumName,
  albumId,
  ownerId,
  links,
  isLoading,
  isCreating,
  isDeleting,
  error,
  onCreateLink,
  onDeleteLink,
  onClearError,
}) => {
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);

  if (!isOpen) return null;

  const generateShareUrl = (token: string) => {
    return buildBrowserShareUrl({
      baseUrl: window.location.origin,
      ownerId,
      albumId,
      token,
    });
  };

  const handleCopyLink = async (link: AlbumLink) => {
    const shareUrl = generateShareUrl(link.token);
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopiedLinkId(link.id);
      setTimeout(() => setCopiedLinkId(null), 2000);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  const handleCreateReadOnlyLink = () => {
    onCreateLink("read_only");
  };

  const handleCreateUploadLink = () => {
    onCreateLink("upload");
  };

  const getPermissionLabel = (permission: string) => {
    return permission === "read_only" ? "Read Only" : "Upload";
  };

  const getPermissionIcon = (permission: string) => {
    if (permission === "read_only") {
      return (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      );
    }
    return (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
      </svg>
    );
  };

  return (
    <OverlayRoot data-section="share-links-modal-root" className="fixed inset-0 z-50 flex items-end sm:items-center justify-center overscroll-none">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-2xl max-h-[85vh] jp-sheet rounded-t-3xl sm:rounded-3xl shadow-2xl border border-white/20 flex flex-col animate-slide-up overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-white/10 bg-black/20 backdrop-blur-xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">Share Links</h2>
              <p className="text-sm text-gray-300 mt-1">{albumName}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-200 p-2 hover:bg-white/10 rounded-xl transition-all duration-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="flex-shrink-0 mx-6 mt-4">
            <div className="bg-red-500/20 backdrop-blur-xl border border-red-400/30 text-red-100 px-4 py-3 rounded-xl flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <span className="text-sm font-medium">{error}</span>
              </div>
              <button
                onClick={onClearError}
                className="text-red-300 hover:text-red-100 hover:bg-red-500/20 p-1 rounded-lg transition-all duration-200"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto overscroll-contain px-6 py-4">
          {/* Create Links Section */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-300 mb-3 uppercase tracking-wide">Create New Link</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={handleCreateReadOnlyLink}
                disabled={isCreating}
                className="flex items-center gap-3 px-4 py-3 bg-white/10 hover:bg-white/20 backdrop-blur-xl border border-white/20 rounded-xl transition-all duration-200 disabled:opacity-50 group"
              >
                <div className="p-2 bg-blue-500/20 rounded-lg group-hover:bg-blue-500/30 transition-colors">
                  <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <div className="text-white font-medium">Read Only</div>
                  <div className="text-xs text-gray-400">View photos only</div>
                </div>
              </button>

              <button
                onClick={handleCreateUploadLink}
                disabled={isCreating}
                className="flex items-center gap-3 px-4 py-3 bg-white/10 hover:bg-white/20 backdrop-blur-xl border border-white/20 rounded-xl transition-all duration-200 disabled:opacity-50 group"
              >
                <div className="p-2 bg-[rgba(var(--primary-green-rgb),0.2)] rounded-lg group-hover:bg-[rgba(var(--primary-green-rgb),0.3)] transition-colors">
                  <svg className="w-5 h-5 text-[rgb(var(--primary-green-rgb))]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <div className="text-white font-medium">Upload</div>
                  <div className="text-xs text-gray-400">Upload & view photos</div>
                </div>
              </button>
            </div>
          </div>

          {/* Existing Links Section */}
          <div>
            <h3 className="text-sm font-semibold text-gray-300 mb-3 uppercase tracking-wide">Active Links</h3>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner message="Loading links..." />
              </div>
            ) : links.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                <p className="text-sm">No active share links yet</p>
                <p className="text-xs mt-1">Create a link above to share this album</p>
              </div>
            ) : (
              <div className="space-y-3">
                {links.map((link) => (
                  <div
                    key={link.id}
                    className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-4"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className={`p-1.5 rounded-lg ${link.permission === "read_only" ? "bg-blue-500/20" : "bg-[rgba(var(--primary-green-rgb),0.2)]"}`}>
                          <div className={link.permission === "read_only" ? "text-blue-400" : "text-[rgb(var(--primary-green-rgb))]"}>
                            {getPermissionIcon(link.permission)}
                          </div>
                        </div>
                        <div>
                          <div className="text-white font-medium text-sm">
                            {getPermissionLabel(link.permission)}
                          </div>
                          <div className="text-xs text-gray-400">
                            Created {new Date(link.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => onDeleteLink(link.id)}
                        disabled={isDeleting}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 p-2 rounded-lg transition-all duration-200 disabled:opacity-50"
                        title="Delete link"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>

                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={generateShareUrl(link.token)}
                        readOnly
                        className="flex-1 px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-gray-300 text-sm font-mono focus:outline-none focus:border-[rgba(var(--primary-blue-rgb),0.55)]"
                      />
                      <button
                        onClick={() => handleCopyLink(link)}
                        className="px-4 py-2 jp-fill-green text-white rounded-lg transition-all duration-200 text-sm font-medium flex items-center gap-2"
                      >
                        {copiedLinkId === link.id ? (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Copied!
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            Copy
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-white/10 bg-black/20 backdrop-blur-xl">
          <button
            onClick={onClose}
            className="w-full px-4 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all duration-200 font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </OverlayRoot>
  );
};
